import { useCallback } from 'react';
import { useOnboardingStore } from '../../zustand/onboardingStore';
import { TourType } from '../../types/onboarding';
import { useLocation, useNavigate } from 'react-router';
import { tourRoutes } from '../../components/Onboarding/tourRoutes';

/**
 * Custom hook for onboarding functionality
 * Provides easy access to onboarding state and actions
 */
export const useOnboarding = () => {
  const store = useOnboardingStore();
  const navigate = useNavigate();
  const location = useLocation();

  const {
    run,
    stepIndex,
    tourType,
    completedTours,
    skippedTours,
    isOnboardingEnabled,
    userPreferences,
    lastCompletedAt,
    startTour,
    stopTour,
    resetTour,
    skipTour,
    completeTour,
    setStepIndex,
    toggleOnboarding,
    updatePreferences,
    resetAllTours,
    isTourCompleted,
    isTourSkipped,
    syncTourProgressFromBackend
  } = store;

  // Start a specific tour
  const handleStartTour = useCallback(
    (type: TourType) => {
      if (!isOnboardingEnabled) {
        console.warn('Onboarding is disabled. Enable it first.');
        return;
      }
      const destination = tourRoutes[type];
      if (location.pathname !== destination) {
        navigate(destination);
      }
      startTour(type);
    },
    [startTour, isOnboardingEnabled, location.pathname, navigate]
  );

  // Check if any tour is currently running
  const isTourRunning = run && tourType !== null;

  // Get tour progress percentage
  const getTourProgress = useCallback(() => {
    if (!tourType) return 0;
    const total = Object.keys(TourType).length;
    const completed = completedTours.length;
    return Math.round((completed / total) * 100);
  }, [completedTours, tourType]);

  // Check if user should see onboarding (new user)
  const isNewUser = completedTours.length === 0 && skippedTours.length === 0;

  return {
    // State
    run,
    stepIndex,
    tourType,
    completedTours,
    skippedTours,
    isOnboardingEnabled,
    userPreferences,
    lastCompletedAt,
    isTourRunning,
    isNewUser,

    // Actions
    startTour: handleStartTour,
    stopTour,
    resetTour,
    skipTour,
    completeTour,
    setStepIndex,
    toggleOnboarding,
    updatePreferences,
    resetAllTours,

    // Utilities
    isTourCompleted,
    isTourSkipped,
    getTourProgress,
    syncTourProgressFromBackend
  };
};
